/**
 * Shared display formatters for the admin UI.
 *
 * Eva money values arrive from sf-api as fixed-precision strings (e.g.
 * "12.345600") to avoid float drift on the wire. We only parse them to a
 * Number at the very edge — for display — and never round-trip arithmetic
 * through the float.
 */

/** Currency used across the platform. Override via VITE_DISPLAY_CURRENCY. */
const DISPLAY_CURRENCY = import.meta.env.VITE_DISPLAY_CURRENCY || 'USD';

const EM_DASH = '\u2014';

/**
 * Parse a fixed-precision money string (e.g. "12.345600") to a finite number,
 * or null when the value is absent/empty/non-numeric. Shared by the budget
 * editors so the parse rule stays identical across the company- and per-user
 * controls.
 *
 * @param value - money as a fixed-precision string, or null/undefined
 * @returns the finite number, or null
 */
export function parseMoney(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a money value (string from sf-api, or number) as a localized currency
 * string. Null/undefined/NaN render as an em-dash so empty data never shows a
 * misleading "$0.00".
 *
 * @param value - money as a fixed-precision string or a number
 * @param fractionDigits - decimal places to show (default 2)
 * @returns formatted currency string, or em-dash when not a finite number
 */
export function formatCurrency(
  value: string | number | null | undefined,
  fractionDigits = 2,
): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return EM_DASH;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: DISPLAY_CURRENCY,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/**
 * Format an integer count with thousands separators. Null/undefined/NaN render
 * as an em-dash.
 *
 * @param value - the count
 * @returns formatted integer string, or em-dash when not a finite number
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return EM_DASH;
  if (!Number.isFinite(value)) return EM_DASH;
  return new Intl.NumberFormat().format(value);
}

/**
 * Format a percentage value (already expressed as a percent, e.g. 42.5 → "42.5%").
 * Null/undefined/NaN render as an em-dash so missing data never shows "0%".
 *
 * @param value - the percentage (where 100 = 100%)
 * @param fractionDigits - decimal places to show (default 1)
 * @returns formatted percentage string, or em-dash when not a finite number
 */
export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1,
): string {
  if (value === null || value === undefined) return EM_DASH;
  if (!Number.isFinite(value)) return EM_DASH;
  return `${value.toFixed(fractionDigits)}%`;
}

/**
 * Compute a delta percentage between a current and previous value, guarding
 * against a zero/absent baseline (returns null — callers render em-dash).
 *
 * @param current - current period value
 * @param previous - previous period value
 * @returns signed percentage change, or null when not computable
 */
export function percentChange(
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (
    current === null ||
    current === undefined ||
    previous === null ||
    previous === undefined ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous) ||
    previous === 0
  ) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

/** Em-dash used as the canonical "no data" glyph across the admin UI. */
export { EM_DASH };
